'use client';
import { useCallback, useEffect, useState } from 'react';

import { isServer } from '@/components/shared/utility/browser';

type HistoryOptionsState = {
  state?: any;
  shallow?: boolean;
};

/**
 * Returns an object containing the history state and functions to manipulate the history.
 *
 * @returns An object containing the following properties:
 * - `isBackPressed`: A boolean indicating whether the back button was pressed.
 * - `pathname`: The current URL path.
 * - `state`: The current history state.
 * - `push`: A function to push a new URL onto the history stack.
 * - `replace`: A function to replace the current URL in the history stack.
 */
export const useHistoryState = () => {
  const [isBack, setIsBack] = useState<boolean>(false);
  const [historyState, setHistoryState] = useState<{
    state: any;
    pathname: string;
  } | null>(
    isServer
      ? null
      : {
          state: window.history.state,
          pathname: window.location.pathname,
        }
  );

  const handleSetIsBack = useCallback((state: boolean) => {
    setIsBack(state);
  }, []);

  const handlePopState = useCallback(
    (event: PopStateEvent) => {
      if (!isBack) {
        handleSetIsBack(true);
      }
      setHistoryState({
        state: event?.state,
        pathname: window.location.pathname,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSetIsBack]
  );

  useEffect(() => {
    if (isServer) return;
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]);

  const push = useCallback(
    (url: string, options: HistoryOptionsState = {}) => {
      if (options.shallow) {
        window.history.pushState(options?.state || null, '', url);
        setHistoryState({ state: options?.state || null, pathname: url });
      } else {
        window.location.assign(url);
      }
      handleSetIsBack(false);
    },
    [handleSetIsBack]
  );

  const replace = useCallback(
    (url: string, options: HistoryOptionsState = {}) => {
      if (options.shallow) {
        window.history.replaceState(options?.state || null, '', url);
        setHistoryState({ state: options?.state || null, pathname: url });
      } else {
        window.location.replace(url);
      }
      handleSetIsBack(false);
    },
    [handleSetIsBack]
  );

  return {
    isBackPressed: isBack,
    pathname: historyState?.pathname,
    state: historyState?.state,
    push,
    replace,
  };
};
